import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { 
  Mail, ArrowRight, Loader, ArrowLeft,
  AlertCircle, CheckCircle, TrendingUp, KeyRound
} from 'lucide-react';
import './Auth.css';

const ForgotPasswordPage = ({ onNavigate }) => {
  const { requestPasswordReset } = useAuth();
  
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateForm = () => {
    if (!email) {
      setFormError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError('Please enter a valid email');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!validateForm()) return;

    setIsSubmitting(true);
    const result = await requestPasswordReset(email);
    setIsSubmitting(false);

    if (result.success) {
      setIsSuccess(true);
    } else {
      setFormError(result.error || 'Request failed. Please try again.');
    }
  };

  return (
    <div className="auth-page">
      {/* Background Effects */}
      <div className="auth-bg">
        <div className="auth-bg-gradient" />
        <div className="auth-bg-grid" />
        <div className="auth-bg-glow auth-bg-glow-1" />
        <div className="auth-bg-glow auth-bg-glow-2" />
      </div>

      <div className="auth-container auth-container-centered">
        <div className="auth-form-panel auth-form-panel-solo">
          <div className="auth-form-container">
            {/* Back Button */}
            <button 
              className="auth-back-btn"
              onClick={() => onNavigate && onNavigate('login')}
            >
              <ArrowLeft size={18} />
              <span>Back to login</span>
            </button>

            {/* Logo */}
            <div className="auth-logo auth-logo-centered">
              <div className="auth-logo-icon">
                <TrendingUp size={28} />
              </div>
              <span className="auth-logo-text">ELEMENTAL</span>
            </div>

            {isSuccess ? (
              <div className="auth-success-state">
                <div className="auth-success-icon">
                  <CheckCircle size={48} />
                </div>
                <h2>Check your email</h2>
                <p>
                  We've sent a password reset link to <strong>{email}</strong>. 
                  Please check your inbox and follow the instructions.
                </p>
                <p className="auth-success-note">
                  Didn't receive the email? Check your spam folder or{' '}
                  <button 
                    className="auth-link-inline"
                    onClick={() => setIsSuccess(false)}
                  >
                    try again
                  </button>
                </p>
                <button
                  className="auth-btn auth-btn-secondary"
                  onClick={() => onNavigate && onNavigate('login')}
                >
                  <ArrowLeft size={18} />
                  <span>Back to login</span>
                </button>
              </div>
            ) : (
              <>
                <div className="auth-form-header auth-form-header-centered">
                  <div className="auth-form-icon">
                    <KeyRound size={24} />
                  </div>
                  <h2>Forgot password?</h2>
                  <p>No worries, we'll send you reset instructions.</p>
                </div>

                {formError && (
                  <div className="auth-alert auth-alert-error">
                    <AlertCircle size={18} />
                    <span>{formError}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="auth-form">
                  <div className="auth-input-group">
                    <label htmlFor="email">Email</label>
                    <div className="auth-input-wrapper">
                      <Mail size={18} className="auth-input-icon" />
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        autoComplete="email"
                        disabled={isSubmitting}
                        autoFocus
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="auth-btn auth-btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader size={18} className="auth-spinner" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span>Reset Password</span>
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </form>

                <div className="auth-footer auth-footer-centered">
                  <span>Remember your password?</span>
                  <button
                    type="button"
                    className="auth-link"
                    onClick={() => onNavigate && onNavigate('login')}
                  >
                    Sign in
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;